'use client'

import { ProjectSidebar } from './project-sidebar'
import { UnifiedForm } from '@/capture'
import { UnifiedFeedLazy } from '@/feed'
import { COMPACT_CLASSES } from '@/ui/design-tokens'
import { cn } from '@/lib/tailwind'
import { useProjectStats } from './select-project-stats'
import { useProjects } from './select-projects'
import type { Project, ProjectStatus } from '@/document'

interface ProjectDetailProps {
  project: Project
}

const STATUS_LABELS: Record<ProjectStatus, string> = {
  active: 'Active',
  on_hold: 'On Hold',
  completed: 'Completed',
}

/**
 * Two-column project detail layout.
 * Desktop: sidebar (220px) + content (1fr).
 * Mobile: single column with project header, then capture + feed.
 */
export function ProjectDetail({ project }: ProjectDetailProps) {
  const { data: allStats } = useProjectStats()
  const stats = allStats[project.id] ?? {
    total: 0,
    completed: 0,
    percentage: 0,
    overdue: 0,
  }
  const { data: projects = [] } = useProjects()

  return (
    <div className="mx-auto w-full max-w-3xl px-4 sm:px-6 lg:max-w-5xl xl:max-w-7xl">
      {/* Desktop: two-column grid */}
      <div className="hidden lg:grid lg:grid-cols-[220px_1fr]">
        {/* Left column: sticky sidebar */}
        <div
          id="project-sidebar"
          className="border-border sticky top-0 h-screen overflow-y-auto border-r"
        >
          <ProjectSidebar project={project} />
        </div>

        {/* Right column: quick capture + feed */}
        <div className="mx-auto w-full max-w-2xl space-y-6 py-6">
          <section aria-label="Project capture">
            <UnifiedForm
              enforcedProjectId={project.id}
              availableProjects={projects}
            />
          </section>

          <section aria-label="Project entries">
            <UnifiedFeedLazy projectId={project.id} />
          </section>
        </div>
      </div>

      {/* Mobile: single column */}
      <div className="space-y-6 py-6 lg:hidden">
        {/* Mobile header */}
        <div className="flex items-center gap-3">
          <h1 className="text-foreground font-[family-name:var(--font-instrument-serif)] text-lg">
            {project.name}
          </h1>
          <span
            className={cn(COMPACT_CLASSES.filled, 'shrink-0')}
            aria-label={`Status: ${STATUS_LABELS[project.status]}`}
          >
            {STATUS_LABELS[project.status]}
          </span>
          <span className="text-muted-foreground text-xs tabular-nums">
            {stats.completed}/{stats.total} tasks
          </span>
        </div>

        <section aria-label="Project capture">
          <UnifiedForm
            enforcedProjectId={project.id}
            availableProjects={projects}
          />
        </section>

        <section aria-label="Project entries">
          <UnifiedFeedLazy projectId={project.id} />
        </section>
      </div>
    </div>
  )
}
